import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Button,
  Text,
  Flex,
  Tooltip,
} from '@chakra-ui/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageTabs } from '@partiu/ui/components/organisms/PageTabs.tsx';
import { INotificationRow } from '@partiu/api/notifications/types';
import { DashboardTemplate } from '../components/templates/DashboardTemplate';
import { DataTablePage } from '../components/templates/DataTablePage';
import { DataGridDateFormatter, DataGridDateTimeFormatter } from '../formatters';
import { DAY_LABELS } from './NotificationCentral/Steps/labels';
import {
  NotificationHooks,
  useDeleteAgreementNotification,
  useGetAgreementNotifications,
} from '@partiu/api/notifications/hooks';
import { getImportation } from '@partiu/api/dashboard/api';
import { useTheme } from '@partiu/ui/contexts/ThemeProvider';
import ToggleEmail from '../components/atoms/ToggleEmail';

const TABS = ['config', 'email', 'push'] as const;
const CREATE_PATH = '/central-de-notificacoes/criar';

export function CentralNotification() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabIndex = Math.max(0, TABS.indexOf(searchParams.get('tab') as (typeof TABS)[number]));

  return (
    <DashboardTemplate>
      <PageTabs
        index={tabIndex}
        onChange={(index) => setSearchParams(index ? { tab: TABS[index] } : {}, { replace: true })}
        width="100%"
        maxW="100%"
        tabs={[
          { label: 'Configuração', content: <ConfigTab /> },
          { label: 'E-mails', content: <NotificationsTab type="email" /> },
          { label: 'Push', content: <NotificationsTab type="push" /> },
        ]}
      />
    </DashboardTemplate>
  );
}

const ConfigTab: React.FC = () => {
  const navigate = useNavigate();
  const { colors, integrationId, totalImportedUsers } = useTheme();

  const [, setUserElegible] = useState(0);

  const [status, setStatus] = useState('Não Iníciado');

  const getUsetIntegration = useCallback(async () => {
    const response = await getImportation({
      page: 1,
      pageSize: 10,
      orderBy: 'createdAt',
      orderDirection: 'desc',
    });

    if (response.count === 0) {
      setUserElegible(0);
      setStatus('Não Iníciado');
    } else {
      const data = response?.results[0];
      if (data.status === 'done') {
        setUserElegible(data.users_amount || 0);
        setStatus('Concluído');
      } else {
        setUserElegible(data.users_amount || 0);
        setStatus('Em andamento');
      }
    }
  }, []);

  useEffect(() => {
    getUsetIntegration();
  }, [getUsetIntegration]);

  return (
    <Box width="100%" p={4}>
      {/* <Text fontSize="lg" fontWeight="bold" mb={4}>
        Por favor, escolha qual produto deseja configurar:
      </Text>
      <HStack spacing={4} mb={6}>
        <Button colorScheme="blue" size="lg">
          CLUBE
        </Button>
        <Button colorScheme="blue" size="lg">
          LIVRO
        </Button>
        <Button colorScheme="blue" size="lg">
          REVISTAS
        </Button>
      </HStack> */}
      <Text fontSize="md" color="gray.600" mb={4}>
        Atualmente o seu clube você tem as seguintes configurações:
      </Text>
      <Accordion allowToggle>
        {integrationId && (
          <AccordionItem>
            <h2>
              <AccordionButton>
                <Box fontWeight={'bold'} flex="1" textAlign="left">
                  Total de usuários elegíveis a receber notificações:
                </Box>
                <Text color="green.500" fontWeight="bold" mr={4}>
                  {totalImportedUsers} Usuários
                </Text>
                <Text color={colors.primaryColor} fontWeight="bold" mr={4}>
                  Status: {status}
                </Text>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              <p style={{ marginBottom: '16px' }}>
                Notamos que o seu Convênio está elegível para importar usuários do sistema "{integrationId}" para a
                nossa plataforma. Essa funcionalidade permite que você aproveite ao máximo os recursos disponíveis,
                garantindo uma comunicação mais eficiente com os usuários.
              </p>
              <p style={{ marginBottom: '16px' }}>
                Para iniciar o processo, basta clicar no botão abaixo para importar os usuários. Após a importação, você
                poderá configurar notificações personalizadas para atender às necessidades específicas desses usuários.
              </p>
              <p style={{ marginBottom: '16px' }}>
                Aproveite essa oportunidade para otimizar a gestão e o engajamento dos usuários do seu Convênio.
              </p>

              <ToggleEmail onComplete={getUsetIntegration} />
            </AccordionPanel>
          </AccordionItem>
        )}

        {/*      <AccordionItem>
          <h2>
            <AccordionButton>
              <Box flex="1" textAlign="left">
                Número de usuários com Emails "extraídos" do seu ERP:
              </Box>
              <Text color="red.500" fontWeight="bold" mr={4}>
                1.234
              </Text>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4}>Informações detalhadas sobre os emails extraídos.</AccordionPanel>
        </AccordionItem>

        <AccordionItem>
          <h2>
            <AccordionButton>
              <Box flex="1" textAlign="left">
                Campanhas de estímulo ao uso por email está configurado?
              </Box>
              <Text color="red.500" fontWeight="bold" mr={4}>
                Não
              </Text>
              <Button colorScheme="yellow" size="sm">
                Configurar agora
              </Button>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4}>Informações detalhadas sobre as campanhas de email.</AccordionPanel>
        </AccordionItem>

        <AccordionItem>
          <h2>
            <AccordionButton>
              <Box flex="1" textAlign="left">
                Campanhas de estímulo ao uso por push está configurado?
              </Box>
              <Text color="red.500" fontWeight="bold" mr={4}>
                Não
              </Text>
              <Button colorScheme="yellow" size="sm">
                Configurar agora
              </Button>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4}>Informações detalhadas sobre as campanhas de push.</AccordionPanel>
        </AccordionItem>

        <AccordionItem>
          <h2>
            <AccordionButton>
              <Box flex="1" textAlign="left">
                Campanhas de envio da Revista Digital de Descontos está configurado?
              </Box>
              <Text color="red.500" fontWeight="bold" mr={4}>
                Não
              </Text>
              <Button colorScheme="yellow" size="sm">
                Configurar agora
              </Button>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4}>Informações detalhadas sobre as campanhas de envio da revista.</AccordionPanel>
        </AccordionItem> */}
      </Accordion>
      <Accordion allowToggle>
        <AccordionItem>
          <h2>
            <AccordionButton>
              <Box fontWeight={'bold'}  flex="1" textAlign="left">
                Campanhas de envio de email e push notification:
              </Box>
              <Text color="green.500" fontWeight="bold" mr={4}>
                Sim
              </Text>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4}>
            <p>Aqui você pode configurar campanhas de envio de email e push notification para os usuários elegíveis.</p>{' '}
            <p>
              Essas campanhas podem ser personalizadas para atender às necessidades específicas do seu convênio,
              permitindo que você envie mensagens relevantes e oportunas para os usuários.
            </p>
            <Flex mt="20px">
              <Button
                variant="outline"
                colorScheme="teal"
                width="200px"
                backgroundColor={colors.primaryColor}
                color="white"
                _hover={{ backgroundColor: colors.primaryColor }}
                _active={{ backgroundColor: colors.primaryColor }}
                _focus={{ boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)' }}
                onClick={() => navigate(CREATE_PATH)}
              >
                Começar agora
              </Button>
            </Flex>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Box>
  );
};

const scheduleLabel = (row: INotificationRow) => {
  if (row.scheduleType === 'recurring') {
    const days = (row.daysOfWeek ?? []).map((day) => DAY_LABELS[day]?.slice(0, 3)).join(', ');
    return `${days} às ${row.time?.slice(0, 5) ?? ''}`;
  }
  return row.date ? DataGridDateTimeFormatter({ value: row.date }) : '-';
};

const statusOf = (row: INotificationRow) => {
  if (row.hasError) return { label: 'Erro', colorScheme: 'red' };
  if (!row.active) return { label: 'Inativa', colorScheme: 'gray' };
  if (row.scheduleType === 'one_time' && row.lastTimeSent) return { label: 'Enviada', colorScheme: 'green' };
  return { label: 'Agendada', colorScheme: 'blue' };
};

const NotificationsTab: React.FC<{ type: 'email' | 'push' }> = ({ type }) => {
  const extraFilters = useMemo(() => ({ notification_type: type }), [type]);

  return (
    <DataTablePage
      modelName={'Central de Notificações'}
      columns={[
        {
          field: type === 'email' ? 'emailSubject' : 'pushSubject',
          headerName: type === 'email' ? 'Assunto do e-mail' : 'Título do push',
          flex: 2,
          minWidth: 200,
        },
        {
          field: 'scheduleType',
          headerName: 'Agendamento',
          flex: 1,
          minWidth: 170,
          sortable: false,
          valueGetter: ({ row }) => scheduleLabel(row),
        },
        {
          field: 'status',
          headerName: 'Status',
          width: 120,
          sortable: false,
          renderCell: ({ row }) => {
            const { label, colorScheme } = statusOf(row);
            return (
              <Tooltip label={row.errorLog} isDisabled={!row.errorLog}>
                <Badge colorScheme={colorScheme}>{label}</Badge>
              </Tooltip>
            );
          },
        },
        {
          field: 'lastTimeSent',
          headerName: 'Último envio',
          flex: 1,
          minWidth: 150,
          valueFormatter: DataGridDateTimeFormatter,
        },
        { field: 'createdAt', headerName: 'Criado em', flex: 1, minWidth: 120, valueFormatter: DataGridDateFormatter },
      ]}
      listQueryKey={NotificationHooks.Notifications}
      searchPlaceholder={type === 'email' ? 'Assunto do e-mail' : 'Título do push'}
      addFormLabel="Nova notificação"
      removePadding
      listHook={useGetAgreementNotifications}
      extraFilters={extraFilters}
      addFormPath={CREATE_PATH}
      selectable={true}
      canExport={false}
      deleteMutationHook={useDeleteAgreementNotification}
    />
  );
};

export default CentralNotification;
